/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { dataProvider as dataProviderOld } from 'admin/dataProvider';
import { withDefaultCurrency } from 'common/utils/formatUtils';
import {
  CollisionInsuranceRisk,
  LifeInsuranceApplicant,
  LifeInsuranceInsuranceRisk,
  MortgageCollateral,
  MortgageParticipant,
  PropertyInsuranceInsurance,
  PropertyInsuranceInsuranceRisk,
} from 'generated/graphql';
import _ from 'lodash';
import { DataProvider } from 'react-admin';

import { AdminResourceDataProvider } from './AdminResourceDataProvider';
import { BaseResourceDataProvider, DataProviderOperation } from './BaseResourceDataProvider';
import { ClientAdminResourceDataProvider } from './ClientAdminResourceDataProvider';
import { ProcessResourceDataProvider } from './ProcessResourceDataProvider';
import { ProductResourceDataProvider } from './ProductResourceDataProvider';

/** User related data providers */

class ClientDataProvider extends AdminResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation) {
    return 'id creationTimestamp updateTimestamp user { id email subject name surname phoneNumber displayName creationTimestamp updateTimestamp } status';
  }

  protected transformParams(resource: string, operation: DataProviderOperation, params: any) {
    switch (operation) {
      case 'update':
        return {
          id: params.id,
          updatedClient: {
            status: params.data.status,
          },
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  }
}

class UserDataProvider extends AdminResourceDataProvider {
  protected getFields = () =>
    'id email name surname phoneNumber displayName creationTimestamp updateTimestamp subject roles';

  protected transformParams(resource: string, operation: DataProviderOperation, params: any) {
    switch (operation) {
      case 'create':
        return {
          user: {
            displayName: params.data.displayName,
            email: params.data.email,
            phoneNumber: params.data.phoneNumber,
          },
        };
      case 'update':
        return {
          id: params.id,
          updatedUser: {
            displayName: params.data.displayName,
            email: params.data.email,
            phoneNumber: params.data.phoneNumber,
            roles: params.data.roles,
          },
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  }
}
class ServiceLocationDataProvider extends BaseResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation) {
    return 'id name';
  }
}
class StaffDataProvider extends AdminResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation) {
    return 'id creationTimestamp updateTimestamp user { id email subject name surname phoneNumber displayName roles creationTimestamp updateTimestamp } serviceLocations { id name } serviceAreas';
  }

  protected transformParams(resource: string, operation: DataProviderOperation, params: any) {
    switch (operation) {
      case 'update':
        return {
          id: params.id,
          updatedStaff: {
            serviceAreas: params.data.serviceAreas,
            serviceLocationIds: params.data.serviceLocationIds,
          },
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  }
}

class UserInvitationDataProvider extends AdminResourceDataProvider {
  protected getFields = () =>
    'id inviter { id email subject } email role relationType status statusChangeTimestamp creationTimestamp updateTimestamp';

  protected transformParams(resource: string, operation: DataProviderOperation, params: any) {
    switch (operation) {
      case 'create':
        return {
          userInvitation: {
            email: params.data.email,
            role: params.data.role,
            status: 'PENDING',
          },
        };
      case 'update':
        return {
          id: params.id,
          updatedUserInvitation: {
            email: params.previousData.email,
            role: params.previousData.role,
            status: params.data.status,
          },
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  }
}

class UserRelationDataProvider extends AdminResourceDataProvider {
  protected getFields = () =>
    'id subjectUser { id email subject } objectUser { id email displayName subject } relationType serviceAreas creationTimestamp updateTimestamp';

  protected transformParams(resource: string, operation: DataProviderOperation, params: any) {
    switch (operation) {
      case 'create':
        return {
          userRelation: {
            objectUserId: params.data.objectUserId,
            relationType: params.data.relationType,
            serviceAreas: params.data.serviceAreas,
            subjectUserId: params.data.subjectUserId,
          },
        };
      case 'update':
        return {
          id: params.id,
          updatedUserRelation: {
            objectUserId: params.data.objectUser.id,
            relationType: params.data.relationType,
            serviceAreas: params.data.serviceAreas,
            subjectUserId: params.data.subjectUser.id,
          },
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  }
}

class ReportDataProvider extends AdminResourceDataProvider {
  protected getFields = () =>
    `     
    id 
    client {id user { id email subject }} 
    file { id }
    type
    published    
    creationTimestamp 
    updateTimestamp    
    `;

  protected transformParams(resource: string, operation: DataProviderOperation, params: any) {
    switch (operation) {
      case 'create':
        return {
          report: {
            clientId: params.data.clientId,
            published: params.data.published,
            type: params.data.type,
          },
        };
      case 'update':
        return {
          id: params.id,
          updatedReport: {
            clientId: params.data.client.id,
            published: params.data.published,
            type: params.data.type,
          },
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  }
}

/**
 * Calculator data providers
 */

class MortgageCalculatorCalculationDataProvider extends AdminResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    ` id creationTimestamp 
      client {id user { id email subject }}
      inquiry { 
        numberOfApplicants 
        ageOldestApplicant 
        numberOfDependents 
        salaryIncome { monthlyIncome { amount currency } } 
        entrepreneurshipIncome { yearlyRevenues { amount currency } taxBase { amount currency } taxPayed { amount currency } }
        ltdIncome { yearlyRevenues { amount currency } yearlyProfit { amount currency } equityShares ceoCompensation { amount currency } }
        liabilities { balance { amount currency } monthlyInstallment { amount currency } }
      }
      result { maxMortgageAmount { amount currency } }`;
}

class LifeInsuranceCalculatorCalculationDataProvider extends AdminResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    ` id 
      creationTimestamp 
      client {id user { id email subject }} 
      inquiry { liabilitiesSum financialReserveRisk sportRisk employmentRisk drivingMileage healthRisk } 
      result { score maximalScore necessity significantDomains similarUserRatio }`;
}

/**
 * Process data providers
 */

class MortgagePurchaseProcessDataProvider extends ProcessResourceDataProvider {
  protected getFields(resource: string, operation: DataProviderOperation) {
    return (
      super.getFields(resource, operation) +
      ` acceptedTimestamp acceptedProposal { amount { amount } institutionAbbreviation }`
    );
  }
}

class MortgageRefinanceProcessDataProvider extends ProcessResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` acceptedTimestamp acceptedProposal { amount { amount } institutionAbbreviation }`;
}

class LifeInsuranceAuditProcessDataProvider extends ProcessResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) + ' locked';
}

class LifeInsurancePurchaseProcessDataProvider extends ProcessResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) + ' locked';
}

/**
 * Product data providers
 */

class MortgageProductDataProvider extends ProductResourceDataProvider {
  protected getFields(resource: string, operation: DataProviderOperation): string {
    return (
      super.getFields(resource, operation) +
      ` amount { amount currency } 
        balance { amount currency } 
        interestRate 
        annualPercentageRate 
        fixationEndDate 
        fixationLength 
        financialDetailsUpdatedAt 
        collaterals { address } 
        participants { name }`
    );
  }

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        amount: withDefaultCurrency(params.data.amount),
        balance: withDefaultCurrency(params.data.balance),
        collaterals: params.data.collaterals?.map((collateral: MortgageCollateral) => ({
          address: collateral.address,
        })),
        participants: params.data.participants?.map((participant: MortgageParticipant) => ({
          name: participant.name,
        })),
      },
    };
  };
}

class LoanProductDataProvider extends ProductResourceDataProvider {
  protected getFields(resource: string, operation: DataProviderOperation): string {
    return (
      super.getFields(resource, operation) +
      ` amount { amount currency } 
        balance { amount currency } 
        interestRate 
        annualPercentageRate 
        financialDetailsUpdatedAt`
    );
  }

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        amount: withDefaultCurrency(params.data.amount),
        balance: withDefaultCurrency(params.data.balance),
      },
    };
  };
}

class LifeInsuranceProductDataProvider extends ProductResourceDataProvider {
  protected getFields(resource: string, operation: DataProviderOperation) {
    return (
      super.getFields(resource, operation) +
      ` applicants { 
          id 
          name 
          surname 
          isInsured 
          isInsuring       
          insurance { 
            insuranceRisks { 
              id 
              type
              name 
              annualCost { amount currency } 
              insuredSum { amount currency } 
            } 
          } 
        }
        surrenderValue { amount currency } 
        savings { 
          amount { amount currency } 
          insuredSum { amount currency } 
        } 
        financialDetailsUpdatedAt`
    );
  }

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        applicants: params.data.applicants?.map((applicant: LifeInsuranceApplicant) => ({
          insurance: {
            insuranceRisks:
              applicant.insurance?.insuranceRisks?.map((insuranceRisk: LifeInsuranceInsuranceRisk) => ({
                annualCost: withDefaultCurrency(insuranceRisk.annualCost),
                insuredSum: withDefaultCurrency(insuranceRisk.insuredSum),
                name: insuranceRisk.name ?? '',
                type: insuranceRisk.type ?? '',
              })) ?? [],
          },
          isInsured: applicant.isInsured,
          isInsuring: applicant.isInsuring,
          name: applicant.name,
          surname: applicant.surname,
        })),
        savings: {
          amount: withDefaultCurrency(params.data.savings.amount),
          insuredSum: withDefaultCurrency(params.data.savings.insuredSum),
        },
        surrenderValue: withDefaultCurrency(params.data.surrenderValue),
      },
    };
  };
}

class InvestmentProductDataProvider extends ProductResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` financialInstrument 
      currentMarketValue { amount currency } 
      amountInvested { amount currency } 
      financialDetailsUpdatedAt`;

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        amountInvested: withDefaultCurrency(params.data.amountInvested),
        currentMarketValue: withDefaultCurrency(params.data.currentMarketValue),
      },
    };
  };
}

class MotorLiabilityInsuranceProductDataProvider extends ProductResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` vehicleInfo { 
        type
        manufactureYear 
        licenseNumber 
        owner { dateOfBirth postalCode yearOfLastCollision numberOfRecentCollisions } 
      } 
      insuringPerson { type name }`;
}

class CollisionInsuranceProductDataProvider extends ProductResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` vehicleInfo { 
        type 
        manufactureYear
        licenseNumber 
        owner { dateOfBirth postalCode yearOfLastCollision numberOfRecentCollisions } 
      } 
      insuringPerson { type name }
      insuranceRisks { 
        id 
        type 
        name 
        insuredSum { amount currency }
        annualCost { amount currency }
      }`;

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        insuranceRisks: params.data.insuranceRisks?.map((insuranceRisk: CollisionInsuranceRisk) => ({
          annualCost: withDefaultCurrency(insuranceRisk.annualCost),
          insuredSum: withDefaultCurrency(insuranceRisk.insuredSum),
          name: insuranceRisk.name,
          type: insuranceRisk.type,
        })),
      },
    };
  };
}

class PropertyInsuranceProductDataProvider extends ProductResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` insuredProperty { 
        type 
        address 
        propertyValue { amount currency } 
        householdValue { amount currency } 
      } 
      insuringPerson { name surname } 
      basicInsurances { 
        id 
        type 
        insuredSum { amount currency } 
        annualCost { amount currency } 
        insuranceRisks { 
          id 
          type 
          name 
          insuredSum { amount currency } 
          annualCost { amount currency } 
        } 
      } 
      otherInsurances { 
        id 
        name 
        insuredSum { amount currency } 
        annualCost { amount currency } 
        insuranceRisks { 
          id 
          type 
          name 
          insuredSum { amount currency } 
          annualCost { amount currency } 
        } 
      }`;

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        basicInsurances: params.data.basicInsurances?.map((insurance: PropertyInsuranceInsurance) => ({
          annualCost: withDefaultCurrency(insurance.annualCost),
          insuranceRisks: insurance.insuranceRisks?.map((insuranceRisk: PropertyInsuranceInsuranceRisk) => ({
            annualCost: withDefaultCurrency(insuranceRisk.annualCost),
            insuredSum: withDefaultCurrency(insuranceRisk.insuredSum),
            name: insuranceRisk.name,
            type: insuranceRisk.type,
          })),
          insuredSum: withDefaultCurrency(insurance.insuredSum),
          type: insurance.type,
        })),
        insuredProperty: {
          address: params.data.insuredProperty.address,
          householdValue: withDefaultCurrency(params.data.insuredProperty.householdValue),
          propertyValue: withDefaultCurrency(params.data.insuredProperty.propertyValue),
          type: params.data.insuredProperty.type,
        },
        otherInsurances: params.data.otherInsurances?.map((insurance: PropertyInsuranceInsurance) => ({
          annualCost: withDefaultCurrency(insurance.annualCost),
          insuranceRisks: insurance.insuranceRisks?.map((insuranceRisk: PropertyInsuranceInsuranceRisk) => ({
            annualCost: withDefaultCurrency(insuranceRisk.annualCost),
            insuredSum: withDefaultCurrency(insuranceRisk.insuredSum),
            name: insuranceRisk.name,
            type: insuranceRisk.type,
          })),
          insuredSum: withDefaultCurrency(insurance.insuredSum),
          name: insurance.name,
        })),
      },
    };
  };
}

class CreditCardProductDataProvider extends ProductResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` limitAmount { amount currency } 
      regularUsage 
      owedAmountPaidByMaturity 
      financialDetailsUpdatedAt`;

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        limitAmount: withDefaultCurrency(params.data.limitAmount),
      },
    };
  };
}

class RetirementPensionSavingsProductDataProvider extends ProductResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` currentMarketValue { amount currency } 
      guaranteedFund  
      nonGuaranteedFund 
      voluntaryContribution 
      financialDetailsUpdatedAt`;

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        currentMarketValue: withDefaultCurrency(params.data.currentMarketValue),
      },
    };
  };
}

class SupplementaryPensionSavingsProductDataProvider extends ProductResourceDataProvider {
  protected getFields = (resource: string, operation: DataProviderOperation) =>
    super.getFields(resource, operation) +
    ` currentMarketValue { amount currency } 
      guaranteedFund  
      nonGuaranteedFund 
      possibleEmployerContribution 
      employerContribution { amount currency } 
      employeeContribution { amount currency } 
      financialDetailsUpdatedAt`;

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    const inputValues = super.transformParams(resource, operation, params);
    return {
      ...inputValues,
      ...{
        currentMarketValue: withDefaultCurrency(params.data.currentMarketValue),
        employeeContribution: withDefaultCurrency(params.data.employeeContribution),
        employerContribution: withDefaultCurrency(params.data.employerContribution),
      },
    };
  };
}

class GenericProductDataProvider extends ProductResourceDataProvider {}

/**
 * Other data providers
 */

class AccountDataProvider extends ClientAdminResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation): string {
    return super.getFields(resource, operation) + ' consentExpired accessUntil';
  }
}

class FinancialAdvisorTaskDataProvider extends ClientAdminResourceDataProvider {
  protected getCreateInputParamName() {
    return 'task';
  }

  protected getOperationName(resource: string, operation: DataProviderOperation): string {
    switch (operation) {
      case 'delete':
        return 'adminDeactivateFinancialAdvisorTask';
      default:
        return super.getOperationName(resource, operation);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation): string {
    return ` 
        id 
        client {id user { id email subject }} 
        creationTimestamp 
        title 
        subtitle 
        text 
        dueDate 
        doneDate 
        isActive 
        stage 
        details {... on ProductFinancialAdvisorTaskDetails { productId productType } }`;
  }
}

class InstitutionDataProvider extends BaseResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation): string {
    return 'id name';
  }
}

class FinancialReserveDataProvider extends ClientAdminResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation): string {
    return ` 
        id 
        client {id user { id email subject }} 
        creationTimestamp
        updateTimestamp
        institution { id name }
        institutionName
        text
        amount { amount currency }
       `;
  }

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    switch (operation) {
      case 'create':
        return {
          clientId: params.data.clientId,
          [this.getInputFieldName(resource, operation)]: _.merge(
            _.omit(params.data, this.getDataFieldsToExclude(resource, operation, params)),
            {
              amount: withDefaultCurrency(params.data.amount),
            },
          ),
        };
      case 'update':
        return {
          id: params.data.id,
          [this.getInputFieldName(resource, operation)]: _.merge(
            _.omit(
              params.data,
              this.getDataFieldsToExclude(resource, operation, params).concat(['amount', 'institution']),
            ),
            {
              amount: withDefaultCurrency(params.data.amount),
              institutionId: params.data.institution?.id,
            },
          ),
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  };
}

class CashFlowAdjustmentDataProvider extends ClientAdminResourceDataProvider {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getFields(resource: string, operation: DataProviderOperation): string {
    return ` 
        id 
        client {id user { id email subject }} 
        creationTimestamp
        updateTimestamp
        text
        period { 
          periodTypeCode
          periodCount
          referenceDate
          expectedAmount { amount currency }
        }
     `;
  }

  protected transformParams = (resource: string, operation: DataProviderOperation, params: any): any => {
    switch (operation) {
      case 'create':
        return {
          clientId: params.data.clientId,
          [this.getInputFieldName(resource, operation)]: _.merge(
            _.omit(params.data, this.getDataFieldsToExclude(resource, operation, params)),
            {
              period: {
                expectedAmount: withDefaultCurrency(params.data.period.expectedAmount),
                periodCount: params.data.period.periodCount,
                periodTypeCode: params.data.period.periodTypeCode,
                referenceDate: params.data.period.referenceDate,
              },
            },
          ),
        };
      case 'update':
        return {
          id: params.data.id,
          [this.getInputFieldName(resource, operation)]: _.merge(
            _.omit(params.data, this.getDataFieldsToExclude(resource, operation, params).concat(['period'])),
            {
              period: {
                expectedAmount: withDefaultCurrency(params.data.period.expectedAmount),
                periodCount: params.data.period.periodCount,
                periodTypeCode: params.data.period.periodTypeCode,
                referenceDate: params.data.period.referenceDate,
              },
            },
          ),
        };
      default:
        return super.transformParams(resource, operation, params);
    }
  };
}

class SalvePerformanceDataDataProvider extends AdminResourceDataProvider {
  protected getResourcePlural(resource: string): string {
    return resource;
  }

  protected getFields(resource: string, operation: DataProviderOperation): string {
    return 'id userId email name surname sentUserInvitationsCount acceptedUserInvitationsCount accountClientsCount finoptioReportClientsCount productClientsCount fintelligenceReportClientsCount';
  }

  protected getFilterInput(resource: string, operation: DataProviderOperation, params: any): any {
    const filterInput = {
      email: params?.filter?.email,
      endDate: !!params?.filter?.endDate ? new Date(params?.filter?.endDate) : undefined,
      role: params?.filter?.role,
      startDate: !!params?.filter.startDate ? new Date(params?.filter.startDate) : undefined,
      surname: params?.filter?.surname,
    };
    return filterInput;
  }

  protected getParamTypes(resource: string, operation: DataProviderOperation): Record<string, string> {
    switch (operation) {
      case 'getList':
        return {
          filterInput: 'PerformanceFilterInput',
          pageInput: 'PageInput',
          sortInput: 'SortInput',
        };
      case 'getMany':
        return {
          filterInput: 'PerformanceFilterInput',
        };
      case 'getManyReference':
        return {
          filterInput: 'PerformanceFilterInput',
          pageInput: 'PageInput',
          sortInput: 'SortInput',
        };
      default:
        return super.getParamTypes(resource, operation);
    }
  }
}

/**
 * Export all registered data providers
 */
export const resourceDataProviders: Record<string, DataProvider> = {
  CashFlowAdjustment: new CashFlowAdjustmentDataProvider(),
  Client: new ClientDataProvider(),
  FinancialReserve: new FinancialReserveDataProvider(),
  Report: new ReportDataProvider(),
  SalvePerformanceData: new SalvePerformanceDataDataProvider(),
  Staff: new StaffDataProvider(),
  User: new UserDataProvider(),
  UserInvitation: new UserInvitationDataProvider(),
  UserRelation: new UserRelationDataProvider(),
};
